import { BehaviorSubject, Subject } from "rxjs";
import { tradingApi } from "../api/trading";
import { TickerInfo } from "../config/tickers";
import { TradingAccountType, TradingOrder, TradingOrderType } from "../config/trading";
import { EditingAccount } from "./editingAccount";
import { EditingPosition } from "./editingPosition";
import { hub } from "../hub";
import { getInt, setVal, VS } from "../storage";
import { TradingSignal } from "./tradingSignal";
import { TVSyncer } from "../tv/sync";
import { PositionChange, SelectionType, TradingSync } from "../tv/trading";
import { EditingOrder } from "./editingOrder";

export interface TraderConfig {
    extraDepo: number;
    hasExtraDepo: boolean;
    risk: number;
    rr: number;
    paperDepo: number;
}

const currentAccId = 'trd_accId';
export class TradingHub {

    //private positionDataChanged: Subject<EditingPosition> = new Subject<EditingPosition>();
    private positionsChanged: Subject<EditingPosition> = new Subject<EditingPosition>();
    private loadingChange: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private accountsChange: Subject<void> = new Subject<void>();
    private accountChange: BehaviorSubject<EditingAccount> = new BehaviorSubject<EditingAccount>(null);
    private positionChanged: Subject<EditingPosition> = new Subject<EditingPosition>();

    private _config: TraderConfig = {
        extraDepo: 100000,
        paperDepo: 100000,
        hasExtraDepo: false,
        risk: 0.5,
        rr: 3
    };
    private _account: EditingAccount;
    private _accounts = new Map<number, EditingAccount>();
    private signal: TradingSignal;
    private inited = false;
    private paperAccount: EditingAccount;

    // getPositionDataChanged = () => this.positionDataChanged.asObservable();
    getPositionsChanged = () => this.positionsChanged.asObservable();
    getPositionChanged = () => this.positionChanged.asObservable();
    getAccountChange = () => this.accountChange.asObservable();
    getLoadingChange = () => this.loadingChange.asObservable();
    getAccountsChange = () => {
        this.prepare();
        return this.accountsChange.asObservable();
    }

    get accounts() { return this._accounts; }
    get account() { return this._account; }
    get position() { return this.account.current; }
    get config() { return this._config; }
    constructor(private syncer: TVSyncer) {
        this.paperAccount = new EditingAccount({
            id: 0,
            name: 'Virtual',
            type: TradingAccountType.Virtual,
            uId: 0,
            apiKey: '',
            apiSecret: ''
        }, this._config);
        this._accounts.set(this.paperAccount.id, this.paperAccount);
        this.signal = new TradingSignal();
        this.signal.getPriceChange().subscribe(tick => this._account.updateMarketPrice(tick));
    }

    async prepare() {
        if (this.inited)
            return;
        this.paperAccount.account.uId = hub.userinfo.uid;
        this.reloadAccounts();
    }

    async reloadAccounts() {
        this.onLoadingChange(true);
        const accounts = await tradingApi.getAccounts().finally(() => this.onLoadingChange(false));
        accounts.forEach(acc => {
            let eacc = this._accounts.get(acc.id);
            if (!eacc) this._accounts.set(acc.id, eacc = new EditingAccount(acc, this._config));
            else eacc.updateAccount(acc);
        });
        this._account = this._accounts.get(getInt(currentAccId, VS.brwsr) ?? 0) ?? this.paperAccount;
        this.accountsChange.next();
        await this.setCurrentAccount(this._account.id);
    }
    async setCurrentAccount(id: number) {
        this._account = this._accounts.get(id) ?? this.paperAccount;
        setVal(currentAccId, this._account.id.toString(), VS.brwsr);
        this.refreshCurrentAccount();
    }

    subsribeToTicker(ticker: TickerInfo) {
        if (!ticker)
            return;
        this.signal.subscribe([ticker.id]);
    }

    async refreshCurrentAccount() {
        this.onLoadingChange(true);
        await this._account.refreshAccountInfo().then(() => this.onLoadingChange(false));
        this.refreshDrawings();
        this.accountChange.next(this.account);
        this.positionChanged.next(this.position);
        const tids = new Set<string>();
        this.account.positions.forEach(x => tids.add(x.tid));
        this.signal.subscribe(Array.from(tids));
    }

    validateNewPrice(x: PositionChange) {
        switch (x.type) {
            case SelectionType.Start:
            case SelectionType.End:
            case SelectionType.Level: {
                if (!x.order)
                    return;
                const d = 10.0 / x.pos.lotSize;//5 points
                if (x.pos.long) {
                    if (x.order.trd.type == TradingOrderType.Stop) {
                        var p = x.pos.entry.price;
                        if (x.pos.targets.length > 0 && p > x.pos.targets[0].price)
                            p = x.pos.targets[0].price;
                        if (x.newPrice > p - d)
                            x.newPrice = p - d;
                    } else {
                        if (x.newPrice < x.pos.stop.price + d)
                            x.newPrice = x.pos.stop.price + d;
                    }
                } else {
                    if (x.order.trd.type == TradingOrderType.Stop) {
                        var p = x.pos.entry.price;
                        if (x.pos.targets.length > 0 && p < x.pos.targets[0].price)
                            p = x.pos.targets[0].price;
                        if (x.newPrice < p + d)
                            x.newPrice = p + d;
                    } else {
                        if (x.newPrice > x.pos.stop.price - d)
                            x.newPrice = x.pos.stop.price - d;
                    }
                }
            } break;
        }
    }
    onInited(sync: TradingSync): void {
        sync.getBeginChange().subscribe(x => {
            this.validateNewPrice(x);
            switch (x.type) {
                case SelectionType.Level:
                    if (x.order)
                        x.order.price = x.newPrice;
                    break;
                case SelectionType.Start:
                    if (x.order)
                        x.order.price = x.newPrice;
                    x.pos.start = x.newPos;
                    break;
                case SelectionType.End:
                    if (x.order)
                        x.order.price = x.newPrice;
                    x.pos.end = x.newPos;
                    break;
                case SelectionType.Move:
                    for (let o of x.move.origs)
                        o.order.price = (o.price - x.move.orig.p) + x.newPrice;
                    x.pos.start = (x.move.start - x.move.orig.t) + x.newPos;
                    x.pos.end = (x.move.end - x.move.orig.t) + x.newPos;
                    break;
            }
            x.pos.recalInfo();
        });

        sync.getEndChange().subscribe(x => {
            this.syncer.items.forEach(tvd => {
                if (tvd.trading.tvd != x.tvd && tvd.trading.pos == x.pos)
                    tvd.trading.refresh();
            });
            this.positionChanged.next(x.pos);
        });
    }
    private onLoadingChange(b: boolean) {
        this.syncer.items.forEach(x => x.trading.setEditing(!b));
        this.loadingChange.next(b);
    }
    private refreshDrawings() {
        this.syncer.items.forEach(x => x.trading.refresh());
    }

    public deleteOrder(order: EditingOrder) {
        order.delete();
        order.pos.recalInfo();
        this.refreshDrawings();
        this.positionChanged.next(order.pos);
        return true;
    }
    public addOrder(pos: EditingPosition, order: EditingOrder, before: boolean) {
        const newOrder: TradingOrder = {
            id: 0,
            market: true,
            type: TradingOrderType.TakeProfit,
            price: null,
            closePosition: false
        };
        let ind = pos.targets.findIndex(x => x == order);
        const stopMoveSize = pos.entry.price - pos.stop.price;
        if (ind == -1) {
            ind = pos.targets.length;
            newOrder.price = pos.targets[pos.targets.length - 1].price + stopMoveSize;
        } else if (before) {
            if (ind == 0)
                newOrder.price = (pos.targets[ind].price + pos.entry.price) / 2;
            else
                newOrder.price = (pos.targets[ind].price + pos.targets[ind - 1].price) / 2;
        } else {
            if (ind == pos.targets.length - 1)
                newOrder.price = pos.targets[ind].price + stopMoveSize / 2;
            else
                newOrder.price = (pos.targets[ind].price + pos.targets[ind + 1].price) / 2;
        }
        pos.addTarget(newOrder).fix = 50;
        pos.recalInfo();
        this.refreshDrawings();
        this.positionChanged.next(pos);
    }

    public refresh(pos: EditingPosition, changed: boolean = false) {
        if (!pos) return;
        pos.recalInfo();
        this.syncer.items.forEach(x => x.trading.refresh());
        if (changed)
            this.positionChanged.next(pos);
    }
    public cancel(pos: EditingPosition) {
        let current = pos == this.account?.current;
        if (pos) {
            pos.account.cancel(pos);
            this.syncer.items.forEach(x => x.trading.delete(pos));
        }
        else {
            for (const acc of this.accounts.values())
                acc.cancel(null);
            this.refreshDrawings();

        }
        this.positionsChanged.next(pos);
        if (current)
            this.positionChanged.next(this.account?.current);
    }
    public createNew = async (tid: string, long: boolean) => {
        this.onLoadingChange(true);
        const pos = await this.account.addNew(tid, long).finally(() => this.onLoadingChange(false));
        this.refreshDrawings();
        this.positionsChanged.next(pos);
        this.positionChanged.next(pos);
        return pos;
    }
    public placePosition = async (pos: EditingPosition, limit: boolean) => {
        this.onLoadingChange(true);
        if (limit)
            pos.entry.setType(TradingOrderType.Limit);
        else
            pos.entry.setType(TradingOrderType.Entry);
        this.account.updatePos(pos).then(x => {
            this.refreshDrawings();
            this.positionChanged.next(pos);
        }).finally(() => this.onLoadingChange(false));
        return pos;
    }
    public select = async (position: EditingPosition) => {
        if (!this.account)
            return;
        this.account.select(position);
        this.refreshDrawings();
        this.positionChanged.next(this.account.current);

    }

}
